'use client';

import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Image as ImageIcon, Info, LibraryBig, Loader2, Plus, Search, Upload, Video as VideoIcon } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useAuthToken } from '@/hooks/useAuthToken';
import { makeId } from '@/lib/editor/editorClips';
import { useEditorStore, type EditorMedia } from '@/lib/editor/editorStore';
import type { StockMediaType, StockSearchResult } from '@/lib/editor/stock';

type PanelTab = 'library' | 'stock';

/**
 * Left "Медиа" tool panel (editor V2 rewrite, task 8; stock media task R6).
 *
 * Ported from the `VideoEditor.tsx` monolith's `MediaLibrary` component +
 * `handleFileChange` upload handler (~lines 895-931, 3304-3392).
 *
 * KNOWN GAP: local uploads create a `blob:` object URL via
 * `URL.createObjectURL` and are added to the store's `library` + timeline
 * immediately, exactly like the monolith did. There is NO S3 upload here —
 * the monolith's separate blob→S3 "upload local media before render/save"
 * step is intentionally not ported in this task; it remains a gap to close
 * in task 9 (save/render needs durable media URLs, not `blob:`).
 *
 * "Стоки" tab (Pexels/Pixabay): search hits our own
 * `/api/editor-assets/stock/search` route (provider keys stay server-side).
 * Clicking a result imports it via `/api/editor-assets/stock/import`, which
 * downloads the asset server-side and re-uploads it into OUR S3 (same
 * presign helper the rest of editor-assets uses) — the response is a
 * durable S3 URL, so the resulting library item is `source: 'upload'` just
 * like a real upload (it IS one, just server-initiated). It is only added
 * to the `library` list, not auto-inserted into the timeline — the user
 * adds it as a clip the same way they add any other library item (click).
 * Photo (image) results land in the library too, but — like the pre-existing
 * upload flow (`accept="video/*"`) — this panel has no image-clip timeline
 * support (`EditorPreview`/`render-worker.ts` treat every clip `src` as a
 * video stream); importing a photo is intentionally scoped to "gets it into
 * our S3 as a library asset", not "renders as a video clip".
 */
export function MediaPanel() {
  const { t } = useI18n();
  const { authedFetch } = useAuthToken();
  const library = useEditorStore((s) => s.library);
  const clips = useEditorStore((s) => s.clips);
  const selectedClipId = useEditorStore((s) => s.selectedClipId);
  const addClip = useEditorStore((s) => s.addClip);
  const setLibrary = useEditorStore((s) => s.setLibrary);
  const addAsset = useEditorStore((s) => s.addAsset);
  const addStickerLayer = useEditorStore((s) => s.addStickerLayer);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<PanelTab>('library');

  // --- Стоки (Pexels/Pixabay) ---
  const [stockQuery, setStockQuery] = useState('');
  const [stockType, setStockType] = useState<StockMediaType>('video');
  const [stockResults, setStockResults] = useState<StockSearchResult[]>([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState<string | null>(null);
  const [missingKeys, setMissingKeys] = useState<string[]>([]);
  const [missingKeysChecked, setMissingKeysChecked] = useState(false);
  const [importingKey, setImportingKey] = useState<string | null>(null);
  // Monotonic request id so a slow, superseded search (or the missing-keys
  // probe, which shares this same path) can't overwrite results/loading
  // state after a newer search has already landed.
  const stockRequestIdRef = useRef(0);

  const selectedMediaId = clips.find((c) => c.id === selectedClipId)?.mediaId;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return library;
    return library.filter((item) => item.title.toLowerCase().includes(q));
  }, [library, query]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    const mediaItems: EditorMedia[] = files.map((file) => ({
      id: makeId('upload'),
      title: file.name,
      src: URL.createObjectURL(file),
      source: 'upload' as const,
      file,
    }));
    setLibrary([...mediaItems, ...library]);
    mediaItems.forEach((media) => addClip(media));
    event.target.value = '';
  };

  const runStockSearch = useCallback(
    async (rawQuery: string, type: StockMediaType) => {
      const q = rawQuery.trim();
      const requestId = ++stockRequestIdRef.current;
      setStockLoading(true);
      setStockError(null);
      try {
        const res = await authedFetch(
          `/api/editor-assets/stock/search?q=${encodeURIComponent(q)}&type=${type}&page=1`
        );
        const json = await res.json();
        // A newer search (or the missing-keys probe) may have started while
        // this request was in flight — a stale response must not clobber
        // whatever the latest request already rendered.
        if (requestId !== stockRequestIdRef.current) return;
        if (!res.ok) throw new Error(json?.error || 'search failed');
        setStockResults(Array.isArray(json?.results) ? json.results : []);
        setMissingKeys(Array.isArray(json?.missingKeys) ? json.missingKeys : []);
      } catch {
        if (requestId !== stockRequestIdRef.current) return;
        setStockResults([]);
        if (q) setStockError(t('studio.editor.stockNoResults'));
      } finally {
        if (requestId === stockRequestIdRef.current) setStockLoading(false);
      }
    },
    [authedFetch, t]
  );

  // Probe for missing provider keys as soon as the tab opens, so the "no
  // keys configured" note shows even before the user types a query
  // (`/stock/search` reports `missingKeys` unconditionally, even for an
  // empty `q`).
  useEffect(() => {
    if (tab !== 'stock' || missingKeysChecked) return;
    setMissingKeysChecked(true);
    const defaultQuery = 'business advertising';
    setStockQuery(defaultQuery);
    runStockSearch(defaultQuery, stockType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, missingKeysChecked]);

  const handleStockSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    runStockSearch(stockQuery, stockType);
  };

  const handleStockTypeChange = (type: StockMediaType) => {
    setStockType(type);
    if (stockQuery.trim()) runStockSearch(stockQuery, type);
  };

  const handleStockImport = async (result: StockSearchResult) => {
    const key = `${result.source}-${result.id}`;
    if (importingKey) return;
    setImportingKey(key);
    setStockError(null);
    try {
      const res = await authedFetch('/api/editor-assets/stock/import', {
        method: 'POST',
        body: JSON.stringify({
          downloadUrl: result.downloadUrl,
          type: result.type,
          source: result.source,
          title: `${result.source} ${result.id}`,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success || !json?.url) {
        throw new Error(json?.error || 'import failed');
      }
      const title = json.title || `${result.source} ${result.type}`;
      if (result.type === 'image') {
        const asset = {
          id: makeId('stock-image'),
          title,
          src: json.url as string,
          s3Key: json.key as string | undefined,
          mimeType: typeof json.contentType === 'string' ? json.contentType : 'image/jpeg',
          createdAt: new Date().toISOString(),
        };
        addAsset(asset);
        addStickerLayer(asset);
      } else {
        const media: EditorMedia = {
          id: makeId('stock-video'),
          title,
          src: json.url as string,
          source: 'upload',
          createdAt: new Date().toISOString(),
        };
        setLibrary([media, ...useEditorStore.getState().library]);
        addClip(media);
      }
    } catch (error) {
      setStockError(error instanceof Error ? error.message : t('studio.editor.stockImportFailed'));
    } finally {
      setImportingKey(null);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#121214] text-white">
      <div className="flex gap-1.5 border-b border-white/10 p-2.5 pb-2">
        <button
          type="button"
          onClick={() => setTab('library')}
          className={`inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-xs font-bold transition ${
            tab === 'library' ? 'bg-brand-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
          }`}
        >
          <LibraryBig className="h-3.5 w-3.5" />
          {t('studio.editor.tabLibrary')}
        </button>
        <button
          type="button"
          onClick={() => setTab('stock')}
          className={`inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-xs font-bold transition ${
            tab === 'stock' ? 'bg-brand-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
          }`}
        >
          <ImageIcon className="h-3.5 w-3.5" />
          {t('studio.editor.tabStock')}
        </button>
      </div>

      {tab === 'library' ? (
        <>
          <div className="border-b border-white/10 p-2.5">
            <div className="flex h-9 items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 text-xs text-white/50 focus-within:border-brand-500/50">
              <Search className="h-3.5 w-3.5 shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('studio.editor.searchMedia')}
                className="min-w-0 flex-1 bg-transparent text-xs text-white placeholder:text-white/40 outline-none"
              />
            </div>
          </div>

          <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto p-2.5">
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mb-2.5 inline-flex h-9 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 text-xs font-black text-white shadow-[0_16px_34px_rgba(91,53,255,0.2)] transition hover:bg-brand-500"
            >
              <Upload className="h-4 w-4" />
              {t('studio.editor.importVideo')}
            </button>

            {filtered.length === 0 ? (
              <p className="rounded-xl border border-dashed border-white/15 p-4 text-center text-xs leading-5 text-white/50">
                {t('studio.editor.emptyLibrary')}
              </p>
            ) : (
              <div className="space-y-2">
                {filtered.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => addClip(item)}
                    className={`group relative flex w-full gap-2 overflow-hidden rounded-xl border bg-white/5 p-1.5 text-left shadow-sm transition ${
                      selectedMediaId === item.id
                        ? 'border-brand-500 ring-4 ring-brand-500/10'
                        : 'border-white/10 hover:border-brand-500'
                    }`}
                  >
                    <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-black">
                      <video src={item.src} className="h-full w-full object-cover" muted preload="metadata" />
                      <span className="absolute left-1.5 top-1.5 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-bold text-white">
                        {item.source === 'upload' ? t('studio.editor.uploaded') : t('studio.editor.studioSource')}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 py-1 pr-8">
                      <p className="line-clamp-2 text-xs font-bold leading-snug text-white">{item.title}</p>
                      <p className="mt-1 text-[11px] font-semibold text-white/50">
                        {item.createdAt ? format(new Date(item.createdAt), 'dd.MM.yyyy') : t('studio.editor.localFile')}
                      </p>
                    </div>
                    <span className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-white opacity-0 shadow-[0_8px_18px_rgba(91,53,255,0.25)] transition group-hover:opacity-100">
                      <Plus className="h-3.5 w-3.5" />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="space-y-2 border-b border-white/10 p-2.5">
            <form
              onSubmit={handleStockSearchSubmit}
              className="flex h-9 items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 text-xs text-white/50 focus-within:border-brand-500/50"
            >
              <Search className="h-3.5 w-3.5 shrink-0" />
              <input
                value={stockQuery}
                onChange={(e) => setStockQuery(e.target.value)}
                placeholder={t('studio.editor.stockSearchPlaceholder')}
                className="min-w-0 flex-1 bg-transparent text-xs text-white placeholder:text-white/40 outline-none"
              />
              <button
                type="submit"
                aria-label="Найти в стоках"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-600 text-white transition hover:bg-brand-500"
              >
                {stockLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
              </button>
            </form>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => handleStockTypeChange('image')}
                className={`inline-flex h-7 flex-1 items-center justify-center gap-1.5 rounded-lg text-[11px] font-bold transition ${
                  stockType === 'image' ? 'bg-white/15 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                }`}
              >
                <ImageIcon className="h-3 w-3" />
                {t('studio.editor.stockPhotos')}
              </button>
              <button
                type="button"
                onClick={() => handleStockTypeChange('video')}
                className={`inline-flex h-7 flex-1 items-center justify-center gap-1.5 rounded-lg text-[11px] font-bold transition ${
                  stockType === 'video' ? 'bg-white/15 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                }`}
              >
                <VideoIcon className="h-3 w-3" />
                {t('studio.editor.stockVideos')}
              </button>
            </div>
          </div>

          <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto p-2.5">
            {missingKeys.length > 0 && (
              <p className="mb-2.5 flex items-start gap-1.5 rounded-xl border border-dashed border-white/15 p-3 text-[11px] leading-4 text-white/50">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {t('studio.editor.stockMissingKeys')}
              </p>
            )}

            {stockLoading ? (
              <div className="flex items-center justify-center py-10 text-white/40">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : stockResults.length === 0 ? (
              stockQuery.trim() || stockError ? (
                <p className="rounded-xl border border-dashed border-white/15 p-4 text-center text-xs leading-5 text-white/50">
                  {stockError || t('studio.editor.stockNoResults')}
                </p>
              ) : null
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {stockResults.map((result) => {
                  const key = `${result.source}-${result.id}`;
                  const isImporting = importingKey === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={isImporting}
                      onClick={() => handleStockImport(result)}
                      className="group relative aspect-video overflow-hidden rounded-lg border border-white/10 bg-black/40 transition hover:border-brand-500 disabled:cursor-wait"
                    >
                      {result.type === 'image' ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={result.thumb} alt="" className="h-full w-full object-cover" loading="lazy" />
                      ) : (
                        <video
                          src={result.previewUrl}
                          poster={result.thumb}
                          className="h-full w-full object-cover"
                          muted
                          preload="metadata"
                        />
                      )}
                      {result.author && (
                        <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1 text-[10px] text-white/70">
                          {result.source} · {result.author}
                        </span>
                      )}
                      <span className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-white opacity-0 shadow-[0_8px_18px_rgba(91,53,255,0.25)] transition group-hover:opacity-100">
                        <Plus className="h-3.5 w-3.5" />
                      </span>
                      {isImporting && (
                        <div
                          role="status"
                          aria-label={t('studio.editor.stockImporting')}
                          className="absolute inset-0 flex items-center justify-center bg-black/60"
                        >
                          <Loader2 className="h-4 w-4 animate-spin text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
